import { useEffect, useState } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { ChevronDown, Check, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Input } from '../ui/Input';

export type FontType = 'heading' | 'body';

const HEADING_FONTS: string[] = [
  'Playfair Display',
  'Merriweather',
  'Lora',
  'Cormorant Garamond',
  'Crimson Text',
  'EB Garamond',
  'Libre Baskerville',
  'Source Serif Pro',
  'Tinos',
  'Spectral',
];

const BODY_FONTS: string[] = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Source Sans Pro',
  'Nunito',
  'Work Sans',
  'Poppins',
  'Karla',
  'DM Sans',
];

const CUSTOM_VALUE = '__custom__';

function toGoogleFontsQuery(family: string): string {
  return family.replace(/ /g, '+');
}

function buildGoogleFontsUrl(family: string): string {
  return `https://fonts.googleapis.com/css2?family=${toGoogleFontsQuery(family)}:wght@400;600;700&display=swap`;
}

function injectFontLink(family: string): void {
  if (!family || family === CUSTOM_VALUE) return;
  const url = buildGoogleFontsUrl(family);
  if (document.querySelector(`link[href="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

const PREVIEW_TEXT = 'Le droit d\'aujourd\'hui pour les enjeux de demain.';

interface FontPickerFieldProps {
  label: string;
  type: FontType;
  /** The current font family name (e.g. "Playfair Display" or the custom family) */
  value: string;
  /** URL for custom Google Font (only used when value is not in curated list) */
  customUrl?: string;
  onChange: (family: string) => void;
  onCustomUrlChange?: (url: string) => void;
}

export function FontPickerField({
  label,
  type,
  value,
  customUrl = '',
  onChange,
  onCustomUrlChange,
}: FontPickerFieldProps) {
  const fonts = type === 'heading' ? HEADING_FONTS : BODY_FONTS;
  const isCurated = fonts.includes(value);
  const selectValue = isCurated ? value : CUSTOM_VALUE;
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Injection de la police custom pour la preview (dès que le nom est saisi)
  useEffect(() => {
    if (isCurated && value) {
      injectFontLink(value);
    } else if (!isCurated && value && !customUrl) {
      // Custom sans URL : on essaie Google Fonts automatiquement
      injectFontLink(value);
    }
  }, [value, isCurated, customUrl]);

  // Injection de l'URL custom explicite
  useEffect(() => {
    if (!customUrl) return;
    if (document.querySelector(`link[href="${customUrl}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = customUrl;
    document.head.appendChild(link);
  }, [customUrl]);

  function handleSelectChange(newValue: string) {
    if (newValue === CUSTOM_VALUE) {
      // Switch to custom — keep current custom name if any, else clear
      onChange(isCurated ? '' : value);
    } else {
      onChange(newValue);
      injectFontLink(newValue);
      // Reset custom URL si on repasse sur une curated
      onCustomUrlChange?.('');
    }
  }

  const previewFamily = isCurated ? value : (value || 'sans-serif');

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-neutral-700">{label}</label>

      {/* Radix Select */}
      <RadixSelect.Root value={selectValue} onValueChange={handleSelectChange}>
        <RadixSelect.Trigger
          className={cn(
            'flex items-center justify-between w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white',
            'hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors',
            'text-neutral-900 data-[placeholder]:text-neutral-400',
          )}
          aria-label={label}
        >
          <RadixSelect.Value placeholder="Choisir une police..." />
          <RadixSelect.Icon>
            <ChevronDown className="w-4 h-4 text-neutral-400" aria-hidden />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            className={cn(
              'z-50 overflow-hidden bg-white rounded-cime border border-neutral-200 shadow-lg',
              'animate-in fade-in-0 zoom-in-95',
            )}
            position="popper"
            sideOffset={4}
          >
            <RadixSelect.Viewport className="p-1 max-h-72">
              {fonts.map((font) => (
                <RadixSelect.Item
                  key={font}
                  value={font}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer select-none outline-none',
                    'text-neutral-900 data-[highlighted]:bg-cime-primary/5 data-[highlighted]:text-cime-primary',
                    'data-[state=checked]:font-semibold',
                  )}
                >
                  <RadixSelect.ItemIndicator>
                    <Check className="w-3.5 h-3.5 text-cime-primary" aria-hidden />
                  </RadixSelect.ItemIndicator>
                  <RadixSelect.ItemText>
                    <span style={{ fontFamily: `'${font}', serif` }}>{font}</span>
                  </RadixSelect.ItemText>
                </RadixSelect.Item>
              ))}

              <RadixSelect.Separator className="h-px bg-neutral-100 my-1" />

              <RadixSelect.Item
                value={CUSTOM_VALUE}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer select-none outline-none',
                  'text-neutral-600 italic data-[highlighted]:bg-cime-primary/5 data-[highlighted]:text-cime-primary',
                  'data-[state=checked]:font-semibold',
                )}
              >
                <RadixSelect.ItemIndicator>
                  <Check className="w-3.5 h-3.5 text-cime-primary" aria-hidden />
                </RadixSelect.ItemIndicator>
                <RadixSelect.ItemText>Autre police Google Fonts…</RadixSelect.ItemText>
              </RadixSelect.Item>
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>

      {/* Custom font fields */}
      {!isCurated && (
        <div className="space-y-3 pl-3 border-l-2 border-cime-primary/20">
          <Input
            label="Nom de la police"
            placeholder="Montserrat, Roboto Slab, Manrope..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            hint={
              <span className="flex items-center gap-1.5 text-xs text-neutral-500">
                Saisissez le nom exact d'une police sur
                <a
                  href="https://fonts.google.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 text-cime-primary hover:underline"
                >
                  Google Fonts <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            }
          />

          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-neutral-500 hover:text-cime-primary underline"
          >
            {showAdvanced ? '- Masquer les options avancées' : '+ Options avancées (URL @font-face custom)'}
          </button>

          {showAdvanced && (
            <Input
              label="URL personnalisée (hébergement externe)"
              placeholder="https://example.com/fonts/my-font.css"
              value={customUrl}
              onChange={(e) => onCustomUrlChange?.(e.target.value)}
              hint={
                <span className="text-xs text-neutral-500">
                  Pour une police hébergée ailleurs que Google Fonts (doit servir un fichier CSS
                  avec règles @font-face). CORS doit autoriser notre domaine.
                </span>
              }
            />
          )}
        </div>
      )}

      {/* Preview text */}
      <p
        className="text-sm text-neutral-600 italic leading-relaxed"
        style={{ fontFamily: `'${previewFamily}', ${type === 'heading' ? 'Georgia, serif' : 'sans-serif'}` }}
      >
        {PREVIEW_TEXT}
      </p>
    </div>
  );
}
